import React, { useRef, useState, useEffect, useMemo, useLayoutEffect } from 'react';
import { Paginator } from 'primereact/paginator';
import { Menu } from 'primereact/menu';
import { Button } from 'primereact/button';

const InitiativeManagementListing = ({
    data, onEdit, onDelete,
    onApprove, onReject, onCancelApprove, onRequestEdit, onViewDetail,
    onBulkApprove, onBulkCancelApprove, onBulkDelete,
    isLoading, toast, filters,
    sortConfig, requestSort
}) => {
    const menu = useRef(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedIds, setSelectedIds] = useState([]);

    const [first, setFirst] = useState(0);
    const rows = 20;

    const containerRef = useRef(null);
    const scrollLeftRef = useRef(0);

    const handleScroll = (e) => {
        scrollLeftRef.current = e.target.scrollLeft;
    };

    useLayoutEffect(() => {
        if (containerRef.current && scrollLeftRef.current > 0) {
            containerRef.current.scrollLeft = scrollLeftRef.current;
        }
    }, [data, first]);

    useEffect(() => {
        setFirst(0);
        setSelectedIds([]);
    }, [filters]);

    useEffect(() => {
        setSelectedIds(prev => prev.filter(id => data.some(d => d.Id === id)));

        if (data.length > 0 && first >= data.length) {
            setFirst(Math.max(0, Math.floor((data.length - 1) / rows) * rows));
        } else if (data.length === 0) {
            setFirst(0);
        }
    }, [data, first, rows]);

    useEffect(() => {
        if (selectedItem && !data.some(item => item.Id === selectedItem.Id)) {
            setSelectedItem(null);
        }
    }, [data, selectedItem]);

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedIds(data.map(item => item.Id));
        } else {
            setSelectedIds([]);
        }
    };

    const handleSelectRow = (e, id) => {
        if (e.target.checked) {
            setSelectedIds(prev => [...prev, id]);
        } else {
            setSelectedIds(prev => prev.filter(itemId => itemId !== id));
        }
    };

    const paginatedData = useMemo(() => data.slice(first, first + rows), [data, first, rows]);

    const onPageChange = (event) => {
        setFirst(event.first);
    };

    const formatDate = (dateStr) => {
        if (!dateStr || dateStr.toString().trim() === "" || dateStr === "0001-01-01T00:00:00") return '---';
        try {
            const timestamp = Date.parse(dateStr);
            if (isNaN(timestamp)) {
                return dateStr;
            }
            return new Date(dateStr).toLocaleDateString('vi-VN');
        } catch (e) {
            return '---';
        }
    };

    const handleDownload = (attachmentData) => {
        if (!attachmentData || attachmentData === '[]' || attachmentData === 'null') {
            return toast?.current?.show({ severity: 'info', summary: 'Thông báo', detail: 'Sáng kiến này chưa có tệp minh chứng!' });
        }

        const triggerDownload = async (name, url) => {
            let fileUrl = url;
            if (!fileUrl) {
                return toast?.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Không tìm thấy đường dẫn tệp!' });
            }

            const base64ToBlob = (dataUrl) => {
                const arr = dataUrl.split(',');
                const mime = arr[0].match(/:(.*?);/)?.[1] || 'application/octet-stream';
                const bstr = atob(arr[1]);
                let n = bstr.length;
                const u8arr = new Uint8Array(n);
                while (n--) {
                    u8arr[n] = bstr.charCodeAt(n);
                }
                return new Blob([u8arr], { type: mime });
            };

            if (typeof fileUrl === 'string') {
                fileUrl = fileUrl.trim();
                if (fileUrl.startsWith('/due-bucket') || fileUrl.startsWith('/uploads')) {
                    fileUrl = `https://data.tapchikhkt.vn${fileUrl}`;
                }
            }

            try {
                if (typeof fileUrl === 'string' && fileUrl.startsWith('data:')) {
                    const blob = base64ToBlob(fileUrl);
                    const objectUrl = URL.createObjectURL(blob);

                    const newWindow = window.open(objectUrl, '_blank');
                    if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                        const link = document.createElement('a');
                        link.href = objectUrl;
                        link.target = '_blank';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                    }

                    setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
                    return;
                }

                const newWindow = window.open(fileUrl, '_blank');
                if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                    const link = document.createElement('a');
                    link.href = fileUrl;
                    link.target = '_blank';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }
            } catch (error) {
                console.error("Lỗi khi xử lý mở file:", error);
                toast?.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Không thể hiển thị tệp này do lỗi định dạng!', life: 3000 });
            }
        };

        try {
            if (attachmentData.startsWith('[')) {
                const files = JSON.parse(attachmentData);
                if (!Array.isArray(files) || files.length === 0) {
                    return toast?.current?.show({ severity: 'info', summary: 'Thông báo', detail: 'Sáng kiến này chưa có tệp minh chứng!' });
                }

                if (files.length > 1) {
                    toast?.current?.show({ severity: 'success', summary: 'Thành công', detail: `Đang mở ${files.length} tệp minh chứng... (Vui lòng cho phép Pop-up nếu bị chặn)` });
                }

                files.forEach(f => {
                    const fileUrl = f.url || f.data || f.link || f.Link || f.URL;
                    triggerDownload(f.name, fileUrl);
                });
            } else if (attachmentData.includes('|')) {
                const parts = attachmentData.split('|');
                triggerDownload(parts[0], parts[1]);
            } else {
                triggerDownload('minh-chung', attachmentData);
            }
        } catch (error) {
            console.error("Lỗi đọc tệp minh chứng:", error);
            toast?.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi đọc tệp minh chứng!' });
        }
    };

    const getStatusStyle = (status) => {
        switch (status) {
            case 'Đã duyệt': return { backgroundColor: '#27ae60', color: '#fff' };
            case 'Đã gửi duyệt': return { backgroundColor: '#3498db', color: '#fff' };
            case 'Từ chối': return { backgroundColor: '#e74c3c', color: '#fff' };
            case 'Yêu cầu chỉnh sửa': return { backgroundColor: '#9b59b6', color: '#fff' };
            case 'Lưu nháp': return { backgroundColor: '#f39c12', color: '#fff' };
            default: return { backgroundColor: '#95a5a6', color: '#fff' };
        }
    };

    const itemTemplate = (item, options) => (
        <div className={options.className} onClick={(e) => {
            if (e.originalEvent) e.originalEvent.preventDefault();
            options.onClick(e);
        }} style={{ display: 'flex', alignItems: 'center', padding: '10px 15px', pointerEvents: item.disabled ? 'none' : 'auto', opacity: item.disabled ? 0.5 : 1, transition: 'background-color 0.2s', cursor: item.disabled ? 'default' : 'pointer' }}>
            <i className={item.icon} style={{ color: item.color, width: '24px', fontSize: '1.1rem', marginRight: '8px' }}></i><span style={{ fontSize: '14px', fontWeight: 500, color: '#495057' }}>{item.label}</span>
        </div>
    );

    const getSortIcon = (columnKey) => {
        if (!sortConfig || sortConfig.key !== columnKey) {
            return <i className="fa-solid fa-sort" style={{ color: '#ccc', marginLeft: '6px', fontSize: '13px' }}></i>;
        }
        return sortConfig.direction === 'asc'
            ? <i className="fa-solid fa-sort-up" style={{ color: '#0056b3', marginLeft: '6px', fontSize: '13px' }}></i>
            : <i className="fa-solid fa-sort-down" style={{ color: '#0056b3', marginLeft: '6px', fontSize: '13px' }}></i>;
    };

    const menuItems = useMemo(() => {
        if (!selectedItem) return [];
        const isLocked = selectedItem.Status === 'Đã gửi duyệt' || selectedItem.Status === 'Đã duyệt';
        const items = [];

        items.push({
            label: 'Xem chi tiết',
            icon: 'fa-solid fa-eye',
            color: '#3498db',
            command: () => onViewDetail && onViewDetail(selectedItem),
            template: itemTemplate
        });
        items.push({ separator: true });

        if (selectedItem.Status !== 'Đã duyệt') {
            items.push({ label: 'Phê duyệt', icon: 'fa-solid fa-check-circle', color: '#27ae60', command: () => onApprove(selectedItem.Id), template: itemTemplate });
            items.push({ label: 'Từ chối', icon: 'fa-solid fa-times-circle', color: '#e74c3c', command: () => onReject(selectedItem.Id), template: itemTemplate });
            items.push({ label: 'Yêu cầu chỉnh sửa', icon: 'fa-solid fa-envelope-open-text', color: '#9b59b6', command: () => onRequestEdit(selectedItem), template: itemTemplate });
        } else {
            items.push({ label: 'Hủy duyệt', icon: 'fa-solid fa-undo', color: '#f39c12', command: () => onCancelApprove(selectedItem.Id), template: itemTemplate });
        }

        items.push({ separator: true });
        items.push({ label: 'Tải minh chứng', icon: 'fa-solid fa-download', color: '#3498db', command: () => handleDownload(selectedItem.AttachmentUrl), template: itemTemplate });
        items.push({ label: 'Chỉnh sửa', icon: 'fa-solid fa-pen-to-square', color: '#3498db', disabled: isLocked, command: () => onEdit(selectedItem), template: itemTemplate });
        items.push({ label: 'Xóa', icon: 'fa-solid fa-trash-can', color: '#e74c3c', disabled: isLocked, command: () => onDelete(selectedItem.Id), template: itemTemplate });

        return items;
    }, [selectedItem, onApprove, onReject, onRequestEdit, onCancelApprove, onEdit, onDelete]);

    return (
        <>
            {selectedIds.length > 0 && (
                <div style={{
                    padding: '12px 16px',
                    background: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: '12px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ background: '#e0f2fe', padding: '8px', borderRadius: '50%', display: 'flex', width: '36px', height: '36px', justifyContent: 'center', alignItems: 'center' }}>
                            <i className="fa-solid fa-list-check" style={{ color: '#0284c7', fontSize: '16px' }}></i>
                        </div>
                        <span style={{ fontSize: '15px', color: '#475569' }}>
                            Đã chọn <strong style={{ color: '#0f172a', fontSize: '18px', margin: '0 4px' }}>{selectedIds.length}</strong> mục
                        </span>
                    </div>

                    <div style={{
                        display: 'flex',
                        gap: '8px',
                        flexWrap: 'wrap'
                    }}>
                        <button
                            onClick={() => onBulkApprove && onBulkApprove(selectedIds)}
                            style={{
                                backgroundColor: '#10b981', color: 'white', border: 'none',
                                padding: '8px 12px', borderRadius: '6px', fontSize: '13px',
                                fontWeight: '500', cursor: 'pointer', display: 'flex',
                                alignItems: 'center', gap: '6px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#059669'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                            onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#10b981'; e.currentTarget.style.transform = 'translateY(0)'; }}
                        >
                            <i className="fa-solid fa-check-circle" style={{ fontSize: '12px' }}></i> Phê duyệt
                        </button>

                        <button
                            onClick={() => onBulkCancelApprove && onBulkCancelApprove(selectedIds)}
                            style={{
                                backgroundColor: '#f59e0b', color: 'white', border: 'none',
                                padding: '8px 12px', borderRadius: '6px', fontSize: '13px',
                                fontWeight: '500', cursor: 'pointer', display: 'flex',
                                alignItems: 'center', gap: '6px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#d97706'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                            onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#f59e0b'; e.currentTarget.style.transform = 'translateY(0)'; }}
                        >
                            <i className="fa-solid fa-undo" style={{ fontSize: '12px' }}></i> Hủy duyệt
                        </button>

                        <button
                            onClick={() => onBulkDelete && onBulkDelete(selectedIds)}
                            style={{
                                backgroundColor: '#ef4444', color: 'white', border: 'none',
                                padding: '8px 12px', borderRadius: '6px', fontSize: '13px',
                                fontWeight: '500', cursor: 'pointer', display: 'flex',
                                alignItems: 'center', gap: '6px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#dc2626'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                            onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#ef4444'; e.currentTarget.style.transform = 'translateY(0)'; }}
                        >
                            <i className="fa-solid fa-trash-can" style={{ fontSize: '12px' }}></i> Xóa
                        </button>
                    </div>
                </div>
            )}

            <div
                className="table-card"
                ref={containerRef}
                onScroll={handleScroll}
                style={{
                    overflowX: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    background: '#fff',
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    paddingBottom: '10px',
                    position: 'relative'
                }}
            >
                {isLoading && (
                    <div style={{
                        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.6)',
                        zIndex: 10, display: 'flex', justifyContent: 'center', paddingTop: '60px'
                    }}>
                        <i className="fa-solid fa-circle-notch fa-spin fa-2x" style={{ color: '#3498db', marginBottom: '10px' }}></i>
                    </div>
                )}

                {data.length === 0 && !isLoading ? (
                    <div style={{ textAlign: 'center', padding: '60px 20px', color: '#666' }}>
                        <i className="fa-regular fa-lightbulb" style={{ fontSize: '60px', color: '#f1c40f', marginBottom: '15px' }}></i>
                        <h3 style={{ color: '#e74c3c', margin: '0 0 10px 0' }}>Không tìm thấy dữ liệu</h3>
                    </div>
                ) : (
                    <>
                        <table className="custom-table" style={{ minWidth: '1550px' }}>
                            <thead>
                                <tr>
                                    <th width="3%" style={{ textAlign: 'center' }}>
                                        <input
                                            type="checkbox"
                                            style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                                            checked={data.length > 0 && selectedIds.length === data.length}
                                            onChange={handleSelectAll}
                                        />
                                    </th>
                                    <th width="3%" style={{ textAlign: 'center' }}>STT</th>
                                    <th
                                        width="24%"
                                        onClick={() => requestSort('Name')}
                                        style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                                        title="Nhấn để sắp xếp theo Tên sáng kiến"
                                    >
                                        SÁNG KIẾN {getSortIcon('Name')}
                                    </th>
                                    <th width="10%">CẤP SÁNG KIẾN</th>
                                    <th width="12%">CHỦ TRÌ</th>
                                    <th
                                        width="8%"
                                        onClick={() => requestSort('ApprovalDate')}
                                        style={{ textAlign: 'center', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                                        title="Nhấn để sắp xếp theo Ngày xét duyệt"
                                    >
                                        NGÀY XÉT DUYỆT {getSortIcon('ApprovalDate')}
                                    </th>
                                    <th width="8%" style={{ textAlign: 'center' }}>NGÀY CẬP NHẬT</th>
                                    <th width="10%">NGƯỜI DUYỆT</th>
                                    <th width="8%" style={{ textAlign: 'center' }}>TRẠNG THÁI</th>
                                    <th width="15%">GHI CHÚ</th>
                                    <th width="5%" style={{ textAlign: 'center' }}>CHỨC NĂNG</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedData.map((item, index) => {
                                    let members = [];
                                    try { members = JSON.parse(item.MembersJSON || '[]'); } catch (e) { members = []; }
                                    const memberSummary = members.map(m => m.fullName + (m.isMainAuthor ? ' (Tác giả chính)' : '')).join(', ');

                                    return (
                                        <tr key={item.Id} style={{ backgroundColor: selectedIds.includes(item.Id) ? '#f4f9ff' : 'transparent' }}>
                                            <td style={{ textAlign: 'center' }}>
                                                <input
                                                    type="checkbox"
                                                    style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                                                    checked={selectedIds.includes(item.Id)}
                                                    onChange={(e) => handleSelectRow(e, item.Id)}
                                                />
                                            </td>
                                            <td style={{ textAlign: 'center' }}>{first + index + 1}</td>
                                            <td>
                                                <div
                                                    style={{
                                                        fontWeight: '500',
                                                        color: '#0056b3',
                                                        cursor: 'pointer',
                                                        textDecoration: 'underline'
                                                    }}
                                                    onClick={() => {
                                                        if (item.Status === 'Đã duyệt' || item.Status === 'Đã gửi duyệt') {
                                                            onViewDetail && onViewDetail(item);
                                                        } else {
                                                            onEdit && onEdit(item);
                                                        }
                                                    }}
                                                >
                                                    {item.Name}
                                                </div>
                                                <div style={{ fontSize: '13px', color: '#7f8c8d', fontStyle: 'italic', marginTop: '4px' }}>
                                                    {memberSummary || "Chưa có tác giả"}
                                                </div>
                                            </td>
                                            <td>{item.InitiativeLevelName || item.InitiativeLevel || '---'}</td>
                                            <td>{item.HostInstitution || '---'}</td>
                                            <td style={{ textAlign: 'center' }}>{formatDate(item.ApprovalDate)}</td>

                                            <td style={{ textAlign: 'center' }}>
                                                {formatDate(item.UpdatedDate || item.UpdatedAt || item.updatedDate || item.CreatedDate || item.CreatedAt)}
                                            </td>

                                            <td>
                                                {item.ApproverEmail ? (
                                                    <>
                                                        <div style={{ fontSize: '12px', fontWeight: '500', color: '#333' }}>{item.ApproverName}</div>
                                                        <div style={{ fontSize: '11px', color: '#7f8c8d' }}>{item.ApproverEmail}</div>
                                                    </>
                                                ) : (
                                                    <span style={{ color: '#adb5bd', fontSize: '12px', fontStyle: 'italic' }}>Chưa có</span>
                                                )}
                                            </td>

                                            <td style={{ textAlign: 'center' }}>
                                                <span style={{ ...getStatusStyle(item.Status), padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                                                    {item.Status}
                                                    {(item.Status === 'Từ chối' || item.Status === 'Yêu cầu chỉnh sửa') && item.Note && (
                                                        <i className="fa-solid fa-circle-info" style={{ cursor: 'help', fontSize: '12px' }} title={item.Note}></i>
                                                    )}
                                                </span>
                                            </td>
                                            <td style={{ maxWidth: '250px', wordBreak: 'break-word', whiteSpace: 'normal' }}>
                                                {(item.Status === 'Từ chối' || item.Status === 'Yêu cầu chỉnh sửa') && item.Note ? item.Note : '---'}
                                            </td>
                                            <td style={{ textAlign: 'center' }}>
                                                <Button
                                                    icon="fa-solid fa-ellipsis-vertical"
                                                    className="p-button-rounded p-button-text p-button-plain"
                                                    style={{ color: '#6c757d', width: '2.5rem', height: '2.5rem', padding: 0 }}
                                                    onClick={(e) => { setSelectedItem(item); menu.current.toggle(e); }}
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {data.length > rows && (
                            <div style={{ marginTop: '15px', borderTop: '1px solid #e9ecef', paddingTop: '10px' }}>
                                <Paginator first={first} rows={rows} totalRecords={data.length} onPageChange={(e) => setFirst(e.first)} />
                            </div>
                        )}
                    </>
                )}
            </div>
            <Menu model={menuItems} popup ref={menu} id="popup_menu" appendTo={document.body} style={{ width: '220px', borderRadius: '8px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)' }} />
        </>
    );
};

export default InitiativeManagementListing;